import { apiService, AppStructure } from './apiService';
import type { PublicApp, FileItem, DownloadResponse, DownloadRequest, ApiError } from '../types/api';

export class PublicApiService {
  // Get public app information
  static async getPublicApp(id: string): Promise<PublicApp> {
    try {
      return await apiService.getPublicApp(id);
    } catch (error) {
      throw this.handleError(error, `Failed to fetch public app: ${id}`);
    }
  }

  // Get app structure for public app (replaces getPublicAppFiles)
  static async getPublicAppStructure(id: string): Promise<AppStructure> {
    try {
      return await apiService.getPublicAppStructure(id);
    } catch (error) {
      throw this.handleError(error, `Failed to fetch structure for public app: ${id}`);
    }
  }

  // Get all files from app structure (helper method for backward compatibility)
  static async getPublicAppFiles(id: string): Promise<FileItem[]> {
    try {
      const structure = await this.getPublicAppStructure(id);
      
      // Flatten all files from the structure
      const allFiles: FileItem[] = [...structure.files];
      
      // Recursively get files from folders
      const extractFilesFromFolders = (folders: any[]): FileItem[] => {
        const files: FileItem[] = [];
        folders.forEach(folder => {
          files.push(...folder.files);
          if (folder.folders && folder.folders.length > 0) {
            files.push(...extractFilesFromFolders(folder.folders));
          }
        });
        return files;
      };
      
      allFiles.push(...extractFilesFromFolders(structure.folders));
      
      return allFiles;
    } catch (error) {
      throw this.handleError(error, `Failed to fetch files for public app: ${id}`);
    }
  }

  // Get download URL for public file
  static async getPublicFileDownloadUrl(
    id: string, 
    fileId: string, 
    options?: DownloadRequest
  ): Promise<DownloadResponse> {
    try {
      return await apiService.getPublicFileDownloadUrl(id, fileId, options);
    } catch (error) {
      throw this.handleError(error, `Failed to get download URL for file: ${fileId}`);
    }
  }

  // Download public file
  static async downloadPublicFile(
    id: string, 
    fileId: string, 
    options?: DownloadRequest
  ): Promise<Blob> {
    try {
      const downloadResponse = await this.getPublicFileDownloadUrl(id, fileId, options);
      
      // Add Range header if provided for chunked downloads
      const headers: Record<string, string> = {};
      if (downloadResponse.rangeHeader) {
        headers['Range'] = downloadResponse.rangeHeader;
      }
      
      return await apiService.downloadFile(downloadResponse.url, headers);
    } catch (error) {
      throw this.handleError(error, `Failed to download file: ${fileId}`);
    }
  }

  // Download public file with progress tracking
  static async downloadPublicFileWithProgress(
    id: string,
    fileId: string,
    onProgress?: (loaded: number, total: number) => void,
    options?: DownloadRequest
  ): Promise<Blob> {
    try {
      const downloadResponse = await this.getPublicFileDownloadUrl(id, fileId, options);
      
      // Add Range header if provided for chunked downloads
      const headers: Record<string, string> = {};
      if (downloadResponse.rangeHeader) {
        headers['Range'] = downloadResponse.rangeHeader;
      }
      
      // Use fetch for progress tracking
      const response = await fetch(downloadResponse.url, { headers });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      
      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let loaded = 0;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        loaded += value.length;
        
        if (onProgress && total > 0) {
          onProgress(loaded, total);
        }
      }

      // Combine chunks into a single blob
      return new Blob(chunks);
    } catch (error) {
      throw this.handleError(error, `Failed to download file with progress: ${fileId}`);
    }
  }

  // Utility method to save blob as file
  static saveFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Check if a file type is previewable
  static isPreviewable(mimeType: string): boolean {
    const previewableTypes = [
      'image/',
      'text/',
      'application/pdf',
      'application/json',
      'video/',
      'audio/'
    ];
    
    return previewableTypes.some(type => mimeType.startsWith(type));
  }

  // Get file type category
  static getFileTypeCategory(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType.startsWith('text/')) return 'text';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.includes('document') || mimeType.includes('word')) return 'document';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'spreadsheet';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'presentation';
    if (mimeType.includes('zip') || mimeType.includes('archive')) return 'archive';
    return 'other';
  }

  // Get file icon class based on mime type
  static getFileIcon(mimeType: string): string {
    const category = this.getFileTypeCategory(mimeType);
    
    const iconMap: Record<string, string> = {
      image: '🖼️',
      video: '🎥',
      audio: '🎵',
      text: '📄',
      pdf: '📕',
      document: '📝',
      spreadsheet: '📊',
      presentation: '📊',
      archive: '📦',
      other: '📄'
    };
    
    return iconMap[category] || iconMap.other;
  }

  // Format file size
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Format date
  static formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  // Error handler
  private static handleError(error: any, defaultMessage: string): ApiError {
    return {
      error: (error as ApiError).error || defaultMessage,
      status: (error as ApiError).status
    };
  }
} 